import React, { FC, useEffect } from 'react';
import { useFormikContext } from 'formik';
import { differenceBy } from 'lodash';

import { Feature } from 'api';
import { connectionPopup } from 'helpers';
import { PaddedPaper } from 'components';
import { useProtectedClient, useCurrentUser, usePrevious, useSocialConnections } from 'hooks';

import { INetwork, IShare } from '../../types';

import { ActiveAuth, ActiveUnauth, Inactive } from '../';

interface IProps {
  network: INetwork;
}

const Network: FC<IProps> = ({ network }) => {
  const client = useProtectedClient();
  const currentUser = useCurrentUser();
  const connections = useSocialConnections();
  const previousConnections = usePrevious(connections);
  const hasMounted = usePrevious('hasMounted');

  const hasConnection = connections.some(conn => conn.provider === network.provider);
  const isActive = hasConnection || !network.auth;
  const { setFieldValue } = useFormikContext<IShare>();

  useEffect(() => {
    if (!hasMounted) {
      return;
    }

    const [addedNetwork] = differenceBy(connections, previousConnections, 'id');
    setFieldValue(`${addedNetwork.provider}.network_id`, addedNetwork.id);
    setFieldValue(`${addedNetwork.provider}.active`, true);
  }, [connections.length]);

  function handleConnect({ provider }: INetwork) {
    connectionPopup(
      currentUser.id,
      provider,
      (client.features || []).includes(Feature.LegacyAuthDomain)
    );
  }

  function renderInactiveNetwork() {
    return <Inactive network={network} onConnect={() => handleConnect(network)} />;
  }

  function renderActiveNetwork() {
    const accounts = connections.filter(account => account.provider === network.provider);
    const isAuthed = accounts.length && network.auth;

    if (isAuthed) {
      return <ActiveAuth network={network} accounts={accounts} onConnect={handleConnect} />;
    }

    return <ActiveUnauth network={network} />;
  }

  return (
    <PaddedPaper data-testid={network.name}>
      {isActive ? renderActiveNetwork() : renderInactiveNetwork()}
    </PaddedPaper>
  );
};

export default Network;
